import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { withRouterCompat } from "../../utils/index";
import {
  applicationsListV2,
  isLoading,
  userHasRole,
  userId,
} from "../../selectors/index";
import {
  addApplication,
  deleteApplication,
  getAllApplicationsV2,
  getApplicationListMultipleFieldsV2,
  setBreadcrumbs,
  resolveIdentity,
} from "../../actions/index";
import { Modal, Button, Select, Typography, Layout } from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { Navigate } from "react-router-dom";
import { ContentTable, SearchBar } from "@authzsvc/common-react-components";
import {
  all_applications_reader,
  sidebar_groups,
  sidebar_identities,
} from "../../config";
import NewApplicationButton from "./NewApplicationButton";
import { errorAction, types as at } from "@authzsvc/api-lib";
import { showErrorNotification } from "../../utils/index";
import styles from "./Applications.module.css";

const { Content } = Layout;

const { Text } = Typography;

class Applications extends React.Component {
  static propTypes = {
    loading: PropTypes.bool,
    applications: PropTypes.arrayOf(PropTypes.object),
    allApplicationsReader: PropTypes.bool,
    actions: PropTypes.shape({
      getApplicationListMultipleFieldsV2: PropTypes.func,
      getAllApplicationsV2: PropTypes.func,
      addApplication: PropTypes.func,
      setBreadcrumbs: PropTypes.func,
      resolveIdentity: PropTypes.func,
      deleteApplication: PropTypes.func,
    }),
    breadcrumbs: PropTypes.any,
    id: PropTypes.any,
  };

  state = {
    searchAll: false,
    searchValue: "",
    searchType: "Contains",
    redirect: false,
    redirectTo: "",
    defaultSearchLimit: 100,
    mergedApplications: null,
    canAddApp: false,
  };

  async componentDidMount() {
    this.props.actions.setBreadcrumbs(this.props.breadcrumbs);

    const applications =
      await this.props.actions.getApplicationListMultipleFieldsV2({
        sort: "applicationIdentifier:asc",
        limit: this.state.defaultSearchLimit,
      });

    this.setState({
      mergedApplications: applications.payload.data,
    });

    let identity = await this.props.actions.resolveIdentity(this.props.id);
    if (
      identity.payload.data.type === "Person" ||
      identity.payload.data.type === "Service"
    ) {
      this.setState({ canAddApp: true });
    }
  }

  handleDelete = (appToDelete) => {
    const resp = this.props.actions.deleteApplication(appToDelete);

    if (resp && resp.type === errorAction(at.DELETE_APPLICATION)) {
      showErrorNotification(resp.payload.title, resp.payload.data.message);
    }

    this.setState({
      mergedApplications: this.state.mergedApplications.filter(
        (app) => app.id !== appToDelete.id,
      ),
    });
  };

  handleSelect = (id) => {
    this.setState({ redirect: true, redirectTo: id });
  };

  handleSearch = async (searchValue, searchType, rawLimit) => {
    this.setState({ mergedApplications: null });

    const parsed = parseInt(rawLimit);
    const limit = !isNaN(parsed) && parsed > 0 ? parsed : undefined;

    if (searchValue === "") {
      // Fetch all applications when searchValue is empty
      const applications = (
        await this.props.actions.getApplicationListMultipleFieldsV2({
          sort: "applicationIdentifier:asc",
          limit,
        })
      ).payload.data;

      this.setState({
        mergedApplications: this.mergeSearchResults(applications),
      });
      return;
    }

    const filterFields = ["applicationIdentifier", "homePage"];
    const results = await Promise.all(
      filterFields.map(async (f) =>
        this.doSearch(searchValue, searchType, limit, f),
      ),
    );

    this.setState({
      mergedApplications: this.mergeSearchResults(results.flat()),
    });
  };

  mergeSearchResults = (entities) => {
    const results = [];
    if (Array.isArray(entities)) {
      entities.forEach((el) => {
        if (!results.find((res) => el.id === res.id)) {
          results.push(el);
        }
      });
    }
    return results;
  };

  doSearch = async (searchValue, searchType, limit, field) => {
    const searchCriteria = {
      sort: "applicationIdentifier:asc",
      ...(limit !== undefined && { limit }),
      filter: [`${field}:${searchType}:${searchValue}`],
    };

    let response;
    if (this.state.searchAll) {
      response = await this.props.actions.getAllApplicationsV2(searchCriteria);
    } else {
      response =
        await this.props.actions.getApplicationListMultipleFieldsV2(
          searchCriteria,
        );
    }

    return response.payload.data;
  };

  handleSelectAfterChange = (e, { value }) => {
    const isAll = value === "all";

    // Trigger search when filter is changed
    this.setState({ searchAll: isAll }, () => {
      const { searchValue, searchType, defaultSearchLimit } = this.state;

      // Trigger search only if searchValue is present or when switching between "mine" and "all"
      if (searchValue.length > 0 || !isAll) {
        this.handleSearch(searchValue, searchType, defaultSearchLimit);
      }
    });
  };

  handleSearchValueChange = (value) => {
    this.setState({ searchValue: value });
  };

  showDeleteModal = (app) => {
    this.setState({ deleteModalApp: app });
  };

  handleDeleteModalOk = () => {
    if (this.state.deleteModalApp) {
      this.handleDelete(this.state.deleteModalApp);
    }
  };

  handleDeleteModalCancel = () => {
    this.setState({ deleteModalApp: null });
  };

  render() {
    const columns = [
      {
        title: "Identifier",
        dataIndex: "applicationIdentifier",
        key: "applicationIdentifier",
        ellipsis: true,
        sortDirections: ["descend", "ascend"],
        sorter: (a, b) =>
          a.applicationIdentifier.localeCompare(b.applicationIdentifier),
        searchable: true,
        render: (applicationIdentifier) => (
          <a href={`/manage/${applicationIdentifier}`}>
            {applicationIdentifier}
          </a>
        ),
      },
      {
        title: "Name",
        dataIndex: "displayName",
        key: "displayName",
        ellipsis: true,
        searchable: true,
      },
      {
        title: "Homepage",
        dataIndex: "homePage",
        key: "homePage",
        ellipsis: true,
        searchable: true,
      },
      {
        title: "Owner",
        dataIndex: "ownerDisplayName",
        key: "ownerDisplayName",
        ellipsis: true,
        searchable: true,
        render: (ownerDisplayName, applicationData) => (
          <a
            target="_blank"
            rel="noopener noreferrer"
            href={`${sidebar_identities}/identities/${applicationData.ownerUpn}`}
          >
            {ownerDisplayName}
          </a>
        ),
      },
      {
        title: "Administrators",
        dataIndex: "administratorsDisplayName",
        key: "administratorsDisplayName",
        ellipsis: true,
        searchable: true,
        render: (groupDisplayName, applicationData) => (
          <a
            target="_blank"
            rel="noopener noreferrer"
            href={`${sidebar_groups}/group/${applicationData.administratorsGroupIdentifier}/details`}
          >
            {groupDisplayName}
          </a>
        ),
      },
      {
        title: "Actions",
        width: "10%",
        render: (_, app) => {
          return (
            <>
              <Button
                id="applicationEditButton"
                color="blue"
                variant="filled"
                icon={<EditOutlined />}
                onClick={() => this.handleSelect(app.applicationIdentifier)}
              />
              <span style={{ margin: "0 3px" }} />
              <Button
                id="applicationDeleteButton"
                color="red"
                variant="filled"
                danger
                icon={<DeleteOutlined />}
                onClick={() => this.showDeleteModal(app)}
              />
              {this.state.deleteModalApp &&
                this.state.deleteModalApp.id === app.id && (
                  <Modal
                    title={
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 12,
                        }}
                      >
                        <ExclamationCircleOutlined
                          style={{ fontSize: 24, color: "#faad14" }}
                        />
                        <Text strong>Delete Application</Text>
                      </div>
                    }
                    open={true}
                    onOk={this.handleDeleteModalOk}
                    onCancel={this.handleDeleteModalCancel}
                    okText="Delete Application"
                    cancelText="Cancel"
                    okButtonProps={{ danger: true, type: "primary" }}
                    centered
                    closable
                    width={500}
                  >
                    <Text>
                      Are you sure you want to delete{" "}
                      <strong>{app.displayName}</strong>? This action is
                      irreversible.
                    </Text>
                  </Modal>
                )}
            </>
          );
        },
      },
    ];

    const { loading, allApplicationsReader } = this.props;
    const { redirect, redirectTo } = this.state;

    // We are managing a specific application, let's redirect
    if (redirect) {
      let redirectstring = "/manage/" + redirectTo;
      return <Navigate to={redirectstring} replace />;
    }

    const selectAfter = (
      <Select
        defaultValue="mine"
        onChange={this.handleSelectAfterChange}
        popupMatchSelectWidth={false}
        className={styles.selectDropdown}
      >
        <Select.Option value="mine">in my applications</Select.Option>
        <Select.Option value="all">in all applications</Select.Option>
      </Select>
    );

    return (
      <Content style={{ padding: "34px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 16,
          }}
        >
          <h2 style={{ margin: 0 }}>Applications</h2>
          <NewApplicationButton
            redirect={(id) => this.setState({ redirect: true, redirectTo: id })}
            canAdd={this.state.canAddApp}
          />
        </div>

        <SearchBar
          placeholder="Search for applications by id, name or homepage"
          searchValue={this.state.searchValue}
          onSearchValueChange={this.handleSearchValueChange}
          onSearch={this.handleSearch}
          limit={this.state.defaultSearchLimit}
          filter={true}
          addonAfter={allApplicationsReader && selectAfter}
        />
        <div style={{ marginBottom: "10px" }} />
        <ContentTable
          rowKey="applicationIdentifier"
          dataSource={this.state.mergedApplications}
          columns={columns}
          loading={loading}
          searchHelper={this.handleSearch}
        />
      </Content>
    );
  }
}

export default connect(
  (state) => ({
    id: userId(state),
    applications: applicationsListV2(state),
    loading:
      isLoading(state, "getApplicationListMultipleFieldsV2") ||
      isLoading(state, "getAllApplicationsV2"),
    allApplicationsReader: userHasRole(state, all_applications_reader),
  }),
  (dispatch) => ({
    actions: bindActionCreators(
      {
        getApplicationListMultipleFieldsV2,
        getAllApplicationsV2,
        addApplication,
        setBreadcrumbs,
        deleteApplication,
        resolveIdentity,
      },
      dispatch,
    ),
  }),
)(withRouterCompat(Applications));
